/**
 * Autocomplete candidate plumbing for the `[[`/`#` picker (IMPL-07). Pure and DOM-free so it is
 * unit-tested and shared by the client. Wikilinks and tags resolve to the SAME entity namespace
 * (`entityIdFor` slugs the name on the server), so `[[Vickers]]` and `#Vickers` are the same node —
 * these helpers therefore produce ONE unified candidate list for both triggers; the syntax is just a
 * user preference.
 */

/** Minimal shape of a ProseMirror/TipTap JSON node — enough to walk for references. */
export interface DocNodeJSON {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: DocNodeJSON[];
}

/** Collect the distinct names referenced by `[[ ]]` and `#` in a ProseMirror JSON document. */
export function extractDocNames(doc: DocNodeJSON | null | undefined): string[] {
  const out: string[] = [];
  const walk = (node: DocNodeJSON | undefined): void => {
    if (!node) return;
    if (node.type === 'wikilink') {
      const target = (node.attrs?.target as string | undefined)?.trim();
      if (target) out.push(target);
    } else if (node.type === 'tag') {
      const name = (node.attrs?.name as string | undefined)?.trim();
      if (name) out.push(name);
    }
    node.content?.forEach(walk);
  };
  walk(doc ?? undefined);
  return out;
}

/** Case/space-insensitive identity, matching the server's slug well enough to dedupe candidates. */
function normalize(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Merge candidate names from several sources (local doc, remote entity index, seeds) into one list,
 * de-duplicated by normalized identity. The first spelling seen wins, so callers should pass their
 * preferred-casing source first.
 */
export function unifyNames(...lists: Array<readonly string[]>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const list of lists) {
    for (const raw of list) {
      const name = raw.trim();
      if (!name) continue;
      const key = normalize(name);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
  }
  return out;
}

/**
 * Rank candidates for a query: exact/prefix matches first, then substring matches, each group in
 * stable input order. Empty query returns the head of the (already unified) list. Used for both the
 * `[[` and `#` pickers so they behave identically.
 */
export function rankCandidates(names: readonly string[], query: string, limit = 10): string[] {
  const unique = unifyNames(names);
  const q = query.trim().toLowerCase();
  if (!q) return unique.slice(0, limit);
  const prefix: string[] = [];
  const substring: string[] = [];
  for (const name of unique) {
    const lower = name.toLowerCase();
    if (lower.startsWith(q)) prefix.push(name);
    else if (lower.includes(q)) substring.push(name);
  }
  return [...prefix, ...substring].slice(0, limit);
}
